// Pixel-art helpers for things that point or grow: weapon overlays on guards
// (caravan.js) and flying projectiles / bombs (combat.js).
//
// Everything here is drawn on a grid of PIXEL_SCALE-sized cells — the same
// cell size as the guard sprites — through renderer.pixelSprite, so it reads
// as part of the same pixel art: no antialiased lines or circles. Shapes that
// depend on a direction are generated once per quantised direction and
// cached by identity, which is what the renderer's raster cache keys on.
//
// Effects animate off the wall clock (fxClock): render code must never touch
// a game RNG, or the headless simulator (which doesn't render) would drift
// from the game.

export const PIXEL_SCALE = 2;
// Pointing things are re-rasterised per direction at this angular step
// (11.25°): fine enough that the jump between neighbours is a pixel or two
// at the tip, coarse enough to cache every sprite.
export const DIRECTIONS = 32;
// Hysteresis, in steps: a tracked direction moves to a neighbour only once
// the angle is this far past the halfway line, so a target hovering on the
// boundary doesn't make the sprite flicker between two directions.
const DIR_SLACK = 0.3;

export function fxClock() {
  return typeof performance !== 'undefined' ? performance.now() / 1000 : 0;
}

// Direction → one of DIRECTIONS: { i, fx, fy } with i = 0 east, counting
// clockwise (screen y points down). Pass the previous index as `prev` for a
// direction that is tracked frame to frame (see DIR_SLACK).
export function quantiseDir(x, y, prev = null) {
  const step = (Math.PI * 2) / DIRECTIONS;
  const exact = Math.atan2(y, x) / step;
  let i = ((Math.round(exact) % DIRECTIONS) + DIRECTIONS) % DIRECTIONS;
  if (prev !== null && prev !== undefined) {
    let d = Math.abs(exact - prev) % DIRECTIONS;
    if (d > DIRECTIONS / 2) d = DIRECTIONS - d;
    if (d < 0.5 + DIR_SLACK) i = prev;
  }
  return { i, fx: Math.cos(i * step), fy: Math.sin(i * step) };
}

// Cells whose centres fall inside a shape given in local coordinates:
// origin (oc, or), u along (fx, fy), w along the normal (nx, ny).
// `inside(u, w)` decides; `reach` bounds the scan. Rasterising by cell
// centre at the exact angle is the nearest-neighbour rotation of the shape.
export function rasterLocal(oc, or, fx, fy, nx, ny, reach, inside) {
  const out = [];
  for (let r = Math.floor(or - reach); r <= Math.ceil(or + reach); r++) {
    for (let c = Math.floor(oc - reach); c <= Math.ceil(oc + reach); c++) {
      const dx = c - oc;
      const dy = r - or;
      if (inside(dx * fx + dy * fy, dx * nx + dy * ny)) out.push([c, r]);
    }
  }
  return out;
}

// Snap a world coordinate to the centre of its cell on the global grid, so
// an odd-sized sprite drawn there lands on whole cells.
export function snapToCell(v) {
  return Math.floor(v / PIXEL_SCALE) * PIXEL_SCALE + PIXEL_SCALE / 2;
}

// Mutable square cell grid → frozen sprite rows. Coordinates are relative
// to the centre cell; `size` must be odd so the centre is a cell.
export function makeGrid(size) {
  const mid = (size - 1) / 2;
  const rows = Array.from({ length: size }, () => new Array(size).fill(' '));
  const at = (c, r) => [Math.round(c) + mid, Math.round(r) + mid];
  return {
    put(c, r, ch) {
      const [x, y] = at(c, r);
      if (y >= 0 && y < size && x >= 0 && x < size) rows[y][x] = ch;
    },
    get(c, r) {
      const [x, y] = at(c, r);
      return rows[y]?.[x] ?? ' ';
    },
    done: () => rows.map(row => row.join('')),
  };
}

// Step a line along (fx, fy) from t0 to t1 one cell per major-axis step —
// a Bresenham line without doubled corners. Returns the visited cells.
export function stepLine(hc, hr, fx, fy, t0, t1) {
  const k = 1 / Math.max(Math.abs(fx), Math.abs(fy));
  const out = [];
  for (let s = Math.ceil(t0 / k); s * k <= t1 + 1e-6; s++) {
    out.push([Math.round(hc + fx * s * k), Math.round(hr + fy * s * k)]);
  }
  return out;
}

// Ring the given cells with `rim` wherever the grid is still empty.
export function outline(grid, cells, rim) {
  for (const [c, r] of cells) {
    for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      if (grid.get(c + dc, r + dr) === ' ') grid.put(c + dc, r + dr, rim);
    }
  }
}

// --- Glow ---

// A stepped disc with a dithered rim — no gradients. Drawn translucent
// under the thing it lights.
export const GLOW_SMALL = [
  '   o   ',
  '  ooo  ',
  'ooooooo',
  ' ooooo ',
  'ooooooo',
  '  ooo  ',
  '   o   ',
];
export const GLOW_LARGE = [
  '  o o o  ',
  ' o ooo o ',
  'o ooooo o',
  ' ooooooo ',
  'ooooooooo',
  ' ooooooo ',
  'o ooooo o',
  ' o ooo o ',
  '  o o o  ',
];
export const GLOW_FIRE = { 'o': '#ff7a2a' };
export const GLOW_STICKY = { 'o': '#9acd32' };

// --- Arrow ---
//
// One arrow sprite per (direction, variant), shared by the arrow nocked on
// an archer's bow and the one in flight, so "what is on the bow" and "what
// arrives" are literally the same picture. Centred on the head cell.
// Variants are ArrowVariant values (caravan.js): 'plain' | 'fire' | 'sticky'.

export const ARROW_PALETTE = {
  'a': '#6a4a2a', // plain shaft
  'h': '#3a2a12', // plain head
  'f': '#e8dcc0', // fletching
  'o': '#ff9a3a', // fire shaft
  'y': '#ffe070', // fire head
  'k': '#8a2a08', // fire rim
  'g': '#6b8e23', // sticky shaft
  'G': '#b5e550', // sticky glob
  'd': '#2f4a10', // sticky rim
};
const ARROW_LOOK = {
  plain: { shaft: 'a', head: 'h' },
  fire: { shaft: 'o', head: 'y', rim: 'k' },
  sticky: { shaft: 'g', head: 'G', rim: 'd', glob: true },
};
const ARROW_SIZE = 19;
const _arrowCache = new Map();

// Shaft cells behind the head (head excluded), tail last: a stepped line
// 7 cells long along the exact direction.
export function arrowShaft(dir) {
  return stepLine(0, 0, -dir.fx, -dir.fy, 1, 7.2);
}

export function arrowSprite(dir, variant) {
  const key = `${dir.i}|${variant}`;
  let sprite = _arrowCache.get(key);
  if (sprite) return sprite;
  const look = ARROW_LOOK[variant];
  const grid = makeGrid(ARROW_SIZE);
  const shaft = arrowShaft(dir);
  for (const [c, r] of shaft) grid.put(c, r, look.shaft);
  if (variant === 'plain') grid.put(...shaft[shaft.length - 1], 'f');
  const head = [[0, 0]];
  if (look.glob) head.push([1, 0], [-1, 0], [0, 1], [0, -1]);
  else if (look.rim) head.push([Math.round(dir.fx), Math.round(dir.fy)]);
  for (const [c, r] of head) grid.put(c, r, look.head);
  if (look.rim) outline(grid, head, look.rim);
  sprite = grid.done();
  _arrowCache.set(key, sprite);
  return sprite;
}

// Flame tongues licking up off a fire head: three frames.
export const FLAME_PALETTE = { 'F': '#ff5a1a', 'Y': '#ffe070' };
export const FLAME_FRAMES = [
  [
    '  F  ',
    ' FF  ',
    ' FYF ',
    ' FYF ',
    '     ',
  ],
  [
    '  F  ',
    '  FF ',
    ' FYF ',
    ' FYF ',
    '     ',
  ],
  [
    '     ',
    ' F F ',
    ' FYF ',
    ' FYF ',
    '     ',
  ],
];

// --- Discs and rings ---

const _discCache = new Map();
const _ringCache = new Map();

// Filled pixel disc of radius `r` cells in palette key `ch`.
export function discSprite(r, ch = 'o') {
  const key = `${r}|${ch}`;
  let sprite = _discCache.get(key);
  if (sprite) return sprite;
  const grid = makeGrid(2 * r + 1);
  const lim = (r + 0.5) * (r + 0.5);
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) if (x * x + y * y <= lim) grid.put(x, y, ch);
  }
  sprite = grid.done();
  _discCache.set(key, sprite);
  return sprite;
}

// One-cell pixel ring of radius `r` cells: the cells on a disc's edge.
export function ringSprite(r, ch = 'o') {
  const key = `${r}|${ch}`;
  let sprite = _ringCache.get(key);
  if (sprite) return sprite;
  const grid = makeGrid(2 * r + 1);
  const inside = (x, y) => x * x + y * y <= (r + 0.5) * (r + 0.5);
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      if (!inside(x, y)) continue;
      if (!inside(x + 1, y) || !inside(x - 1, y) || !inside(x, y + 1) || !inside(x, y - 1)) {
        grid.put(x, y, ch);
      }
    }
  }
  sprite = grid.done();
  _ringCache.set(key, sprite);
  return sprite;
}

const _layeredCache = new Map();

// Pixel disc of radius `r` cells built from concentric bands, innermost
// first: `layers` = [[fraction of r, palette key], ...]; cells past the last
// band's edge are empty. With `dither`, the outermost cell ring is a
// checkerboard — a soft edge without a gradient. `name` identifies the
// layer recipe in the cache (pass the same name for the same layers).
export function layeredDiscSprite(r, layers, name, dither = true) {
  const key = `${name}|${r}|${dither}`;
  let sprite = _layeredCache.get(key);
  if (sprite) return sprite;
  const grid = makeGrid(2 * r + 1);
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      const d = Math.sqrt(x * x + y * y);
      if (d > r + 0.5) continue;
      if (dither && d > r - 0.5 && (x + y) % 2) continue;
      const band = layers.find(([frac]) => d <= frac * r + 0.5);
      if (band) grid.put(x, y, band[1]);
    }
  }
  sprite = grid.done();
  _layeredCache.set(key, sprite);
  return sprite;
}
